import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React from 'react';
import DeltaPreview from '../../components/DeltaPreview';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import SVGIcon from '../../components/SVGIcon';
import type { EmbedValue } from '../../editor/embed/matchEmbed';
import { EmbedMode, embedUrl } from '../../editor/embed/matchEmbed';
import type { FileValue } from '../../editor/formats/FileBlot';
import { ALT } from '../../editor/image/ImageNode';
import MentionBase, {
  MentionBaseProvider,
} from '../../editor/mentions/MentionBase';
import type { Mention } from '../../editor/types';
import getEditorMentionUrl from '../../editor/utils/getEditorMentionUrl';
import getFirstImageUrl from '../../editor/utils/getFirstImageUrl';
import fileIcon from '../../icons/non-prefilled/16x16/file.svg';
import imageIcon from '../../icons/non-prefilled/16x16/image.svg';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import * as styles from './CommentContext.module.scss';

function parseString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

interface CommentContextProps {
  context: Op[];
}

const CommentContext = ({ context }: CommentContextProps) => {
  const delta = new Delta(context);

  // Generally we have no other key option
  /* eslint-disable react/no-array-index-key */
  return (
    <MentionBaseProvider containerType="comment">
      {delta.map(({ attributes, insert }, i) => {
        if (!insert) {
          return null;
        }
        if (typeof insert === 'string') {
          if (attributes?.comment) {
            return (
              <span key={i} className={styles.highlight}>
                {insert}
              </span>
            );
          }
          return insert;
        }
        const { image, video, embed, mention, file } = insert as {
          image?: unknown;
          video?: unknown;
          embed?: EmbedValue;
          mention?: Mention;
          file?: FileValue;
        };
        const firstImageSource = getFirstImageUrl(image);
        if (firstImageSource) {
          const imageCaption = Array.isArray(image) ? image[0]?.caption : null;
          return (
            <DeltaPreview.Pill
              key={i}
              icon={<SVGIcon size={12}>{imageIcon}</SVGIcon>}
            >
              {imageCaption ?? ALT}
            </DeltaPreview.Pill>
          );
        }
        if (typeof video === 'string') {
          return (
            <MentionBase key={i} type="attachment" content={video} to={video} />
          );
        }
        if (embed) {
          const url = embedUrl(embed, EmbedMode.READONLY);

          if (url) {
            return (
              <MentionBase
                key={i}
                type="external"
                icon={
                  <ExternalBrandLogo
                    inline
                    {...getExternalBrandLogoProps(embed.type)}
                  />
                }
                content={url}
              />
            );
          }
        }
        if (mention) {
          const { type, id, params } = mention;
          const mentionText = parseString(attributes?.['mention-text']);
          const content = parseString(attributes?.content);

          return (
            <MentionBase
              key={i}
              type={mention.type}
              icon={<ExternalBrandLogo {...getExternalBrandLogoProps(type)} />}
              content={
                mentionText ||
                content ||
                getEditorMentionUrl({ id, type, content, params })
              }
            />
          );
        }
        if (file) {
          return (
            <MentionBase
              key={i}
              type="attachment"
              icon={fileIcon}
              content={file.name}
            />
          );
        }
        return <>{(attributes?.content as string | undefined) ?? null}</>;
      })}
    </MentionBaseProvider>
  );
  /* eslint-enable react/no-array-index-key */
};

export default CommentContext;
